import { FontCatalog, FontFace } from './font-catalog'
import { Font } from './rendering'

export type TextLayout = {
  width: number
  height: number
  lines: { text: string; width: number }[]
}

// Mirrors Graphics.TextLayout. Callers pass source text and the font they
// already hold; the face is resolved here so measurement and rendering agree.
// Lines keep their source text; displayText is the renderer-facing view of the
// same glyph selection. Missing glyph metrics are defects.
//
// A finite maxWidth also soft-wraps each source line greedily at the
// line-break opportunities described at wrap(). Graphics.TextLayout has no
// wrapping because the paint op carries the wrapped lines as source lines.
// This is a subset of Unicode line breaking, not Flutter's ICU paragraph
// engine: other UAX #14 classes (CJK ideographs, punctuation, soft hyphens)
// and bidirectional text are not handled.
const SPACE = ' '
const FALLBACK_GLYPH = '?'

export const TextLayout = {
  layout(text: string, font: Font, lineHeight: number, maxWidth = Infinity): TextLayout {
    const face = FontCatalog.resolve(font)
    const { metrics } = face
    const measure = (text: string) => {
      const units = Array.from(glyphs(text, face)).reduce((sum, ch) => {
        const code = ch.codePointAt(0)!
        const width = metrics.widths[String(code)]
        if (width === undefined)
          throw new Error(`Missing ${face.id} width for U+${code.toString(16)}`)
        return sum + width
      }, 0)
      return { text, width: (units * font.size) / metrics.units_per_em }
    }
    const lines = text.split(/\r\n|\r|\n/).flatMap((text) => wrap(text, maxWidth, measure))
    if (!(lineHeight > 0)) lineHeight = 1.1
    return {
      width: Math.max(...lines.map((line) => line.width)),
      height: lines.length * font.size * lineHeight,
      lines
    }
  },

  displayText(text: string, font: Font): string {
    return glyphs(text, FontCatalog.resolve(font))
  }
}

type Line = { text: string; width: number }

// Line-break opportunities are a subset of Unicode UAX #14: after a run of
// breaking spaces (not the no-break spaces U+00A0, U+2007, U+202F, which bind
// their neighbours), and after a hyphen between non-space characters unless a
// digit follows, so "-5" stays whole. Breaking spaces at a soft break end the
// line and are not painted, so a wrapped line never measures or draws them. A
// segment wider than the line breaks between grapheme clusters, so a base
// character and its combining marks always stay together.
const BREAKING_SPACES = '[ \\t\\u1680\\u2000-\\u2006\\u2008-\\u200a\\u205f\\u3000\\u200b]'
const BREAKING_SPACE = new RegExp(`^${BREAKING_SPACES}$`, 'u')
const TRAILING_BREAKING_SPACES = new RegExp(`${BREAKING_SPACES}+$`, 'u')
const GRAPHEMES = new Intl.Segmenter(undefined, { granularity: 'grapheme' })

function wrap(text: string, maxWidth: number, measure: (text: string) => Line): Line[] {
  const whole = measure(text)
  if (whole.width <= maxWidth) return [whole]

  const visible = (text: string) => text.replace(TRAILING_BREAKING_SPACES, '')
  const lines: Line[] = []
  let line = ''
  for (const segment of breakSegments(text)) {
    if (line != '' && measure(visible(line + segment)).width > maxWidth) {
      lines.push(measure(visible(line)))
      line = ''
    }
    line += segment
    if (measure(visible(line)).width <= maxWidth) continue

    let chunk = ''
    for (const grapheme of graphemes(visible(line))) {
      if (chunk != '' && measure(chunk + grapheme).width > maxWidth) {
        lines.push(measure(chunk))
        chunk = ''
      }
      chunk += grapheme
    }
    line = chunk + line.slice(visible(line).length)
  }
  lines.push(measure(visible(line)))
  return lines
}

// Splits text after each line-break opportunity; joining the segments
// restores the text.
function breakSegments(text: string): string[] {
  const clusters = graphemes(text)
  const segments: string[] = []
  let segment = ''
  clusters.forEach((cluster, index) => {
    segment += cluster
    const previous = clusters[index - 1]
    const next = clusters[index + 1]
    if (next === undefined) return
    const endsSpaces = BREAKING_SPACE.test(cluster) && !BREAKING_SPACE.test(next)
    const endsHyphen =
      cluster == '-' &&
      previous !== undefined &&
      !BREAKING_SPACE.test(previous) &&
      !BREAKING_SPACE.test(next) &&
      !/\d/u.test(next)
    if (endsSpaces || endsHyphen) {
      segments.push(segment)
      segment = ''
    }
  })
  segments.push(segment)
  return segments
}

function graphemes(text: string): string[] {
  return Array.from(GRAPHEMES.segment(text), ({ segment }) => segment)
}

// An encoded face composes accents (NFC) so e + U+0301 finds the encodable é,
// shows tabs and other Unicode space separators as an ordinary space, and any
// other unencodable code point as "?". A face without an encoding keeps every
// code point except tab.
function glyphs(text: string, { encoding }: FontFace): string {
  if (encoding == null) return text.replaceAll('\t', SPACE)
  return Array.from(text.normalize('NFC'), (ch) => {
    if (ch == '\t') return SPACE
    if (Object.hasOwn(encoding, String(ch.codePointAt(0)!))) return ch
    if (/\p{Zs}/u.test(ch)) return SPACE
    return FALLBACK_GLYPH
  }).join('')
}
